import { Injectable } from '@angular/core';
import { FactorStrategy } from '../models/factor-strategy.model';
import { Subject, Subscription } from 'rxjs';
import { ApiRequestsService } from './api-requests.service';
import { CurrentBenchmark } from '../models/current-benchmark.model';
import { ClustersService } from './clusters.service';
import { Cluster } from '../models/cluster.model';
import { ListsService } from './lists.service';
import { StrategyParameters } from '../models/strategy-parameters.model';

@Injectable({
  providedIn: 'root'
})
export class StrategiesService {

  currentClusterSubscription: Subscription;
  currentClusterId: number;

  defaultStrategy1: FactorStrategy;
  defaultStrategy1Subject = new Subject<FactorStrategy>();
  defaultStrategy2: FactorStrategy;
  DefaultStrategy2Subject = new Subject<FactorStrategy>();
  defaultStrategy3: FactorStrategy;
  defaultStrategy3Subject = new Subject<FactorStrategy>();

  defaultStrategy: FactorStrategy;
  defaultStrategySubject = new Subject<FactorStrategy>();
  customizedStrategy: FactorStrategy;
  customizedStrategySubject = new Subject<FactorStrategy>();
  strategyBenchmark: CurrentBenchmark;
  strategyBenchmarkSubject = new Subject<CurrentBenchmark>();

  strategyParameters: StrategyParameters;
  strategyParametersSubject = new Subject<StrategyParameters>();

  constructor(private apiRequestsService: ApiRequestsService,
              private clustersService: ClustersService,
              private listsService: ListsService) {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        if (cluster) {
          if (this.currentClusterId==undefined || this.currentClusterId!=cluster.id) {
            this.currentClusterId = cluster.id;
            this.loadDefaultStrategies(cluster);
            if (cluster && cluster.has_strategy) {
              // Here we should load strategy as it already exists
            }
          }
        }
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  emitDefaultStrategy1() {
    // notify subscriptions and provide them with default strategy 1
    console.log('CALL StrategiesService.emitDefaultStrategy1()');
    this.defaultStrategy1Subject.next(this.defaultStrategy1);
  }

  emitDefaultStrategy2() {
    // notify subscriptions and provide them with default strategy 2
    console.log('CALL StrategiesService.emitDefaultStrategy2()');
    this.DefaultStrategy2Subject.next(this.defaultStrategy2);
  }

  emitDefaultStrategy3() {
    // notify subscriptions and provide them with default strategy 3
    console.log('CALL StrategiesService.emitDefaultStrategy3()');
    this.defaultStrategy3Subject.next(this.defaultStrategy3);
  }

  emitDefaultStrategy() {
    // notify subscriptions and provide them with current default strategy
    console.log('CALL StrategiesService.emitDefaultStrategy()');
    this.defaultStrategySubject.next(this.defaultStrategy);
  }

  emitCustomizedStrategy() {
    // notify subscriptions and provide them with current customized strategy
    console.log('CALL StrategiesService.emitCustomizedStrategy()');
    this.customizedStrategySubject.next(this.customizedStrategy);
  }

  emitStrategyBenchmark() {
    // notify subscriptions and provide them with strategy benchmark
    console.log('CALL StrategiesService.emitStrategyBenchmark()');
    this.strategyBenchmarkSubject.next(this.strategyBenchmark);
  }

  emitStrategyParameters() {
    // notify subscriptions and provide them with strategy parameters
    console.log('CALL StrategiesService.emitStrategyParameters()');
    this.strategyParametersSubject.next(this.strategyParameters);
  }

  async loadDefaultStrategies(cluster?: Cluster) {

    const data: any = await this.apiRequestsService.post('default-strategies',{cluster_id: cluster.id});

    if (data.status=='OK') {
      await this.listsService.cleanData(data.data.efw);
      await this.listsService.cleanData(data.data.efw.parameters);
      await this.listsService.cleanData(data.data.minvar);
      await this.listsService.cleanData(data.data.minvar.parameters);
      await this.listsService.cleanData(data.data.efrp);
      await this.listsService.cleanData(data.data.efrp.parameters);
      await this.listsService.cleanData(data.data.benchmark);

      this.strategyParameters = new StrategyParameters();

      let strategy = new FactorStrategy(cluster.id);
      strategy.initFromData(data.data.minvar);
      this.defaultStrategy1 = strategy;
      this.emitDefaultStrategy1();

      strategy = new FactorStrategy(cluster.id);
      strategy.initFromData(data.data.efrp);
      this.defaultStrategy2 = strategy;
      this.emitDefaultStrategy2();

      strategy = new FactorStrategy(cluster.id);
      strategy.initFromData(data.data.efw);
      this.defaultStrategy3 = strategy;
      this.emitDefaultStrategy3();

      this.changeSelectedStrategy(this.defaultStrategy3.strategy_type.alias);

      let benchmark = new CurrentBenchmark(cluster.id);
      benchmark.initFromData(data.data.benchmark);
      this.strategyBenchmark = benchmark;
      this.emitStrategyBenchmark();
    }
  }

  async computeCustomStrategy(parameters: StrategyParameters, cluster: Cluster, do_save: boolean = false) {
    const cluster_id: number = cluster ? cluster.id : undefined;
    if (parameters.is_customized || do_save) {
      const data: any = await this.apiRequestsService.post('custom-strategy',{
        cluster_id: cluster_id,
        parameters: parameters,
        do_save: do_save
      });

      if (data.status=='OK') {
        await this.listsService.cleanData(data.data);

        let strategy = new FactorStrategy(cluster_id);
        strategy.initFromData(data.data);
        this.customizedStrategy = strategy;
        this.emitCustomizedStrategy();
      }
    } else {
      await this.resetCustomStrategy();
    }
  }

  async resetCustomStrategy() {
    await new Promise(resolve => setTimeout(resolve, 1000));
    this.customizedStrategy = undefined;
    this.emitCustomizedStrategy();
  }

  changeSelectedStrategy(strategyTypeAlias: string) {
    for (let strategy of [this.defaultStrategy1, this.defaultStrategy2, this.defaultStrategy3]) {
      if (strategyTypeAlias==strategy.parameters.strategy_type.alias) {
        this.strategyParameters.initFromData(strategy.parameters);
        this.defaultStrategy = strategy;
      }
    };

    this.emitStrategyParameters();
    this.emitDefaultStrategy();
  }

}
